import { isNil } from "lodash";
import { DateTime } from "luxon";

import { naiveDateToISOString, startLessOrEqualToEnd } from "../../../../utils";

import type {
  SubmitQuoteExecuteResponse,
  SubmitQuoteParams,
  SubmitQuotePreviewShipment,
} from "./types";
import type { SubmitMultiShipmentProjectQuoteFormState } from "../../../Carrier/components/MultiShipmentSubmitQuoteForm/types";
import type { SubmitQuoteFormState } from "../../../Carrier/components/SubmitQuoteForm/types";
import type { SubmitQuoteFormShipment } from "../../../QuoteFields/types";

/**
 * Quotes-only shipments carry no carrier-chosen dates — the server stamps today,
 * matching what the in-app quotes-only form sends.
 */
function resolveQuoteDate(value: string, shipmentType: ShipmentType): string {
  if (shipmentType === "QUOTES_ONLY") {
    return DateTime.now().toFormat("yyyy-MM-dd");
  }
  if (value.length === 0) {
    return "";
  }
  return naiveDateToISOString(value);
}

export function validateSingleShipmentQuote(
  formState: SubmitQuoteFormState,
  shipmentType: ShipmentType,
): string | null {
  if (isNil(formState.price) || formState.price <= 0) {
    return "Price must be greater than 0.";
  }
  if (
    shipmentType !== "QUOTES_ONLY" &&
    !startLessOrEqualToEnd(formState.pickupStart, formState.deliveryEta)
  ) {
    return "Pickup start must be before or equal to the delivery ETA.";
  }
  if (formState.equipmentType === "") {
    return "Please select an equipment type.";
  }
  return null;
}

export function validateMultiShipmentProjectQuote(
  formState: SubmitMultiShipmentProjectQuoteFormState,
  loadsAvailable: number | undefined,
): string | null {
  const { price, minimumLoads, maxLoads, pickupStart, deliveryEta } = formState;

  if (isNil(price) || price <= 0) {
    return "Price must be greater than 0.";
  }
  if (!startLessOrEqualToEnd(pickupStart, deliveryEta)) {
    return "Pickup start must be before or equal to Delivery ETA.";
  }
  if (formState.equipmentType === "") {
    return "Equipment type is required.";
  }
  if (isNil(minimumLoads) || minimumLoads < 1) {
    return "Minimum loads must be greater than 0.";
  }
  if (isNil(maxLoads) || maxLoads < 1) {
    return "Max loads must be greater than 0.";
  }
  if (minimumLoads > maxLoads) {
    return "Max loads must be greater than or equal to minimum loads.";
  }
  if (loadsAvailable !== undefined && minimumLoads > loadsAvailable) {
    return `Minimum loads must be less than or equal to available loads (${loadsAvailable}).`;
  }
  return null;
}

export function buildSingleShipmentQuoteParams(
  formState: SubmitQuoteFormState,
  shipment: SubmitQuotePreviewShipment,
): SubmitQuoteParams {
  return {
    equipment_type: formState.equipmentType,
    pickup_start: resolveQuoteDate(formState.pickupStart, shipment.info.type),
    pickup_start_time: null,
    delivery_eta: resolveQuoteDate(formState.deliveryEta, shipment.info.type),
    delivery_eta_time: null,
    price: formState.price,
    currency: formState.currency,
    notes: formState.notes,
    tracking_link_offered: formState.trackingLinkOffered,
    quoting_unit_of_measure: shipment.info.quoting_unit_of_measure,
  };
}

export function buildMultiShipmentProjectQuoteParams(
  formState: SubmitMultiShipmentProjectQuoteFormState,
  shipment: SubmitQuotePreviewShipment,
): SubmitQuoteParams {
  return {
    ...buildSingleShipmentQuoteParams(formState, shipment),
    min_loads_desired: formState.minimumLoads,
    max_loads_desired: formState.maxLoads,
  };
}

/**
 * Adapts the preview's shipment to what the reused quote forms read.
 *
 * `teammate_quotes` is empty on purpose: this payload carries a
 * `teammate_quote_count` and not the names behind it, because the link is a
 * bearer token that can be forwarded. The forms' own teammate alert and names
 * panel therefore stay hidden, and the landing shows its own advisory instead
 * (#2975 §5).
 */
export function toQuoteFormShipment(
  shipment: SubmitQuotePreviewShipment,
): SubmitQuoteFormShipment {
  return { info: shipment.info, teammate_quotes: [] };
}

interface DispatchSubmitQuoteExecuteResultProps {
  response: SubmitQuoteExecuteResponse;
  onSuccess: VoidFunction;
  // A stop the carrier cannot correct here: the shipment or their own account changed.
  onBlockedFailure: (blockedReasonCode: string) => void;
  onRecoverableFailure: (outcomeCode: string | null) => void;
}

/**
 * Every submit-quote execute action (quote, mark and undo no capacity) answers in
 * the same shape, so each caller routes it the same way and only adds its own
 * analytics around the callbacks.
 */
export function dispatchSubmitQuoteExecuteResult(
  props: DispatchSubmitQuoteExecuteResultProps,
): void {
  const { response, onSuccess, onBlockedFailure, onRecoverableFailure } = props;
  if (response.success) {
    onSuccess();
    return;
  }
  if (response.blocked_reason_code != null) {
    onBlockedFailure(response.blocked_reason_code);
    return;
  }
  onRecoverableFailure(response.outcome_code);
}
